import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';

const ADMIN = '/admin';

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function extractListPayload(payload, preferredKeys = []) {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];

  const keysToCheck = [...preferredKeys, 'items', 'data', 'results'];
  for (const key of keysToCheck) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
    if (isPlainObject(value)) {
      const nested = extractListPayload(value, preferredKeys);
      if (nested.length > 0) return nested;
    }
  }

  return [];
}

function extractPaginationPayload(payload) {
  if (!isPlainObject(payload)) {
    return { page: 1, limit: 20, total: 0, totalPages: 0 };
  }

  const source = payload.pagination ?? payload.meta ?? payload;
  const page = Number(source.page ?? source.current_page ?? 1);
  const limit = Number(source.limit ?? source.per_page ?? source.page_size ?? 20);
  const total = Number(source.total ?? source.total_count ?? source.totalCount ?? 0);
  const totalPages = Number(
    source.totalPages ??
      source.total_pages ??
      (limit > 0 ? Math.ceil(total / limit) : 0)
  );

  return {
    page: Number.isFinite(page) ? page : 1,
    limit: Number.isFinite(limit) ? limit : 20,
    total: Number.isFinite(total) ? total : 0,
    totalPages: Number.isFinite(totalPages) ? totalPages : 0,
  };
}

// GET /admin/vendor-bank-account/:userId
export const fetchVendorBankAccountByUserId = createAsyncThunk(
  'vendorBankAccount/fetchVendorBankAccountByUserId',
  async (userId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/vendor-bank-account/${userId}`);
      return data?.data ?? data ?? null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch vendor bank account');
    }
  }
);

// GET /admin/vendor-bank-accounts/:id
export const fetchVendorBankAccountById = createAsyncThunk(
  'vendorBankAccount/fetchVendorBankAccountById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/vendor-bank-accounts/${id}`);
      return data?.data ?? data ?? null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch vendor bank account');
    }
  }
);

// GET /admin/vendor-bank-accounts (query: page, limit)
export const fetchVendorBankAccounts = createAsyncThunk(
  'vendorBankAccount/fetchVendorBankAccounts',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;

      const { data } = await api.get(`${ADMIN}/vendor-bank-accounts`, { params });
      const payload = data?.data ?? data;

      return {
        vendorBankAccounts: extractListPayload(payload, ['items']),
        pagination: extractPaginationPayload(payload),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch vendor bank accounts');
    }
  }
);

const initialState = {
  vendorBankAccounts: [],
  selectedVendorBankAccount: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  loading: false,
  detailLoading: false,
  error: null,
};

const vendorBankAccountSlice = createSlice({
  name: 'vendorBankAccount',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedVendorBankAccount: (state) => {
      state.selectedVendorBankAccount = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendorBankAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorBankAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.vendorBankAccounts = action.payload?.vendorBankAccounts ?? [];
        state.pagination = action.payload?.pagination ?? state.pagination;
      })
      .addCase(fetchVendorBankAccounts.rejected, (state, action) => {
        state.loading = false;
        state.vendorBankAccounts = [];
        state.error = action.payload || 'Failed to fetch vendor bank accounts';
      })
      .addCase(fetchVendorBankAccountById.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorBankAccountById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedVendorBankAccount = action.payload;
      })
      .addCase(fetchVendorBankAccountById.rejected, (state, action) => {
        state.detailLoading = false;
        state.selectedVendorBankAccount = null;
        state.error = action.payload || 'Failed to fetch vendor bank account';
      })
      .addCase(fetchVendorBankAccountByUserId.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorBankAccountByUserId.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedVendorBankAccount = action.payload;
      })
      .addCase(fetchVendorBankAccountByUserId.rejected, (state, action) => {
        state.detailLoading = false;
        state.selectedVendorBankAccount = null;
        state.error = action.payload || 'Failed to fetch vendor bank account';
      });
  },
});

export const { clearError, clearSelectedVendorBankAccount } = vendorBankAccountSlice.actions;
export default vendorBankAccountSlice.reducer;

export const selectVendorBankAccounts = (state) => state.vendorBankAccount.vendorBankAccounts;
export const selectSelectedVendorBankAccount = (state) => state.vendorBankAccount.selectedVendorBankAccount;
export const selectVendorBankAccountPagination = (state) => state.vendorBankAccount.pagination;
export const selectVendorBankAccountLoading = (state) => state.vendorBankAccount.loading;
export const selectVendorBankAccountDetailLoading = (state) => state.vendorBankAccount.detailLoading;
export const selectVendorBankAccountError = (state) => state.vendorBankAccount.error;

export function useVendorBankAccount() {
  const dispatch = useDispatch();
  const vendorBankAccounts = useSelector(selectVendorBankAccounts);
  const selectedVendorBankAccount = useSelector(selectSelectedVendorBankAccount);
  const pagination = useSelector(selectVendorBankAccountPagination);
  const loading = useSelector(selectVendorBankAccountLoading);
  const detailLoading = useSelector(selectVendorBankAccountDetailLoading);
  const error = useSelector(selectVendorBankAccountError);

  return {
    vendorBankAccounts,
    selectedVendorBankAccount,
    pagination,
    loading,
    detailLoading,
    error,
    fetchVendorBankAccounts: useCallback(
      (filters) => dispatch(fetchVendorBankAccounts(filters)),
      [dispatch]
    ),
    fetchVendorBankAccountById: useCallback(
      (id) => dispatch(fetchVendorBankAccountById(id)),
      [dispatch]
    ),
    fetchVendorBankAccountByUserId: useCallback(
      (userId) => dispatch(fetchVendorBankAccountByUserId(userId)),
      [dispatch]
    ),
    clearSelectedVendorBankAccount: useCallback(
      () => dispatch(clearSelectedVendorBankAccount()),
      [dispatch]
    ),
    clearError: useCallback(() => dispatch(clearError()), [dispatch]),
  };
}
